import { useId, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import { useRecoilValue } from "recoil";
import { useTranslation } from "react-i18next";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";
import { useLinesOfBusinessSelection } from "@/hooks/use-lines-of-business-selection";
import { linesOfBusinessState } from "@/store/lines-of-business";

interface ILineOfBusinessSelectorProps {
  /**
   * `inline`: the lines as plain checkboxes laid out in a row (desktop headers).
   * `icon`: icon button with a count badge that opens a checkbox list (mobile header,
   * where there is no room for a row of checkboxes).
   */
  variant?: "inline" | "icon";
}

const InlineSelector = () => {
  const { t } = useTranslation();
  const { neutralColor, primaryColor } = useGomakeTheme();
  const { lines, isSaving, errorKey, toggleLine } = useLinesOfBusinessSelection();
  const headingId = useId();
  const isRtl = t("direction") === "rtl";

  return (
    <Box
      dir={isRtl ? "rtl" : "ltr"}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "4px",
        minWidth: 0,
        flexShrink: 1,
      }}
    >
      <Typography
        id={headingId}
        component="span"
        sx={{
          fontSize: 13,
          fontWeight: 600,
          color: neutralColor(700),
          whiteSpace: "nowrap",
        }}
      >
        {t("linesOfBusiness.selectorTitle")}
      </Typography>
      <Box
        role="group"
        aria-labelledby={headingId}
        aria-label={t("linesOfBusiness.selectorAriaLabel")}
        aria-busy={isSaving}
        data-testid="lob-selector-inline"
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "nowrap",
          gap: "2px 12px",
          minWidth: 0,
          overflowX: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {lines.map((line) => (
          <FormControlLabel
            key={line.id}
            label={line.name}
            title={line.name}
            control={
              <Checkbox
                size="small"
                checked={line.isSelected}
                onChange={() => toggleLine(line.id)}
                sx={{
                  padding: "4px",
                  color: neutralColor(400),
                  "&.Mui-checked": { color: primaryColor(600) },
                }}
              />
            }
            sx={{
              margin: 0,
              flexShrink: 0,
              "& .MuiFormControlLabel-label": {
                fontSize: 13,
                color: neutralColor(700),
                whiteSpace: "nowrap",
                maxWidth: 160,
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            }}
          />
        ))}
      </Box>
      {isSaving && (
        <CircularProgress
          size={14}
          aria-label={t("linesOfBusiness.selectorSaving")}
        />
      )}
      {errorKey && (
        <Typography
          role="alert"
          component="span"
          sx={{ fontSize: 12, color: "error.main", whiteSpace: "nowrap" }}
        >
          {t(errorKey)}
        </Typography>
      )}
    </Box>
  );
};

const IconSelector = () => {
  const { t } = useTranslation();
  const { neutralColor } = useGomakeTheme();
  const { lines, selectedLines, isSaving, errorKey, toggleLine, clearError } =
    useLinesOfBusinessSelection();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const headingId = useId();

  const open = Boolean(anchorEl);
  const isRtl = t("direction") === "rtl";
  const horizontalOrigin = isRtl ? "right" : "left";
  const title = t("linesOfBusiness.selectorTitle");
  const currentLabel =
    selectedLines.length === 1
      ? selectedLines[0].name
      : t("linesOfBusiness.selectorSelectedCount", {
          count: selectedLines.length,
          total: lines.length,
        });
  const ariaLabel = `${t("linesOfBusiness.selectorAriaLabel")}: ${currentLabel}`;

  const handleClose = () => {
    setAnchorEl(null);
    clearError();
  };

  return (
    <>
      <Tooltip title={title} arrow placement="bottom" disableHoverListener={open}>
        <span style={{ display: "inline-flex", minWidth: 0 }}>
          <IconButton
            onClick={(e: React.MouseEvent<HTMLElement>) =>
              setAnchorEl(e.currentTarget)
            }
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-label={ariaLabel}
            data-testid="lob-selector-trigger"
            sx={{
              padding: "4px",
              backgroundColor: "#f6f6f6",
              "&:hover": { backgroundColor: "#ececec" },
            }}
          >
            <Badge
              color="primary"
              badgeContent={isSaving ? undefined : selectedLines.length}
              variant={isSaving ? "dot" : "standard"}
              sx={{
                "& .MuiBadge-badge": { fontSize: 9, minWidth: 14, height: 14 },
              }}
            >
              <AccountTreeOutlinedIcon sx={{ fontSize: 18 }} />
            </Badge>
          </IconButton>
        </span>
      </Tooltip>
      <Popover
        // Keep the portal LTR so MUI's placement is not flipped by html[dir=rtl].
        dir="ltr"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        marginThreshold={8}
        anchorOrigin={{ vertical: "bottom", horizontal: horizontalOrigin }}
        transformOrigin={{ vertical: "top", horizontal: horizontalOrigin }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "8px",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
              border: `1px solid ${neutralColor(200)}`,
              minWidth: 240,
              maxWidth: 360,
              maxHeight: 360,
              marginTop: "4px",
            },
          },
        }}
      >
        <Box dir={isRtl ? "rtl" : "ltr"} sx={{ padding: "8px 12px 12px" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              paddingBottom: "4px",
              borderBottom: `1px solid ${neutralColor(100)}`,
            }}
          >
            <Typography
              id={headingId}
              sx={{ fontSize: 13, fontWeight: 600, color: neutralColor(700) }}
            >
              {title}
            </Typography>
            {isSaving && (
              <CircularProgress
                size={14}
                aria-label={t("linesOfBusiness.selectorSaving")}
              />
            )}
          </Box>
          <Box
            role="group"
            aria-labelledby={headingId}
            aria-label={t("linesOfBusiness.selectorAriaLabel")}
            aria-busy={isSaving}
            sx={{ display: "flex", flexDirection: "column", paddingTop: "4px" }}
          >
            {lines.map((line) => (
              <FormControlLabel
                key={line.id}
                label={line.name}
                control={
                  <Checkbox
                    size="small"
                    checked={line.isSelected}
                    onChange={() => toggleLine(line.id)}
                  />
                }
                sx={{
                  margin: 0,
                  "& .MuiFormControlLabel-label": {
                    fontSize: 13,
                    color: neutralColor(700),
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              />
            ))}
          </Box>
          {errorKey && (
            <Alert severity="error" role="alert" sx={{ marginTop: 1, fontSize: 12 }}>
              {t(errorKey)}
            </Alert>
          )}
        </Box>
      </Popover>
    </>
  );
};

/**
 * Header control that lets a user choose which lines of business are active for the session.
 * Renders nothing when the print house has no lines (`linesOfBusinessState` is empty).
 */
const LineOfBusinessSelector = ({
  variant = "inline",
}: ILineOfBusinessSelectorProps) => {
  const lines = useRecoilValue(linesOfBusinessState);
  if (lines.length === 0) {
    return null;
  }
  return variant === "icon" ? <IconSelector /> : <InlineSelector />;
};

export { LineOfBusinessSelector };
